import styled, { css, type RuleSet } from 'styled-components';
import Heading from 'styles/typography/Heading';
import { getColor, type Theme } from 'styles/theme';
import { breakpoint, font } from 'styles/globals';
import { span } from 'utils/stringUtils';
import { SectionVariant } from 'interfaces/Section';

const layoutStyling: {
  [breakpoint: string]: {
    [variant: string]: RuleSet<any>;
  };
} = {
  [SectionVariant.Section]: {
    initial: css`
      display: flex;
      flex-direction: column;
    `,
    lgMin: css<{ $isReversed: boolean }>`
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-areas: 
        "${span('title', 2)}"
        "${span('pinned', 2)}"
        "${span('hero', 1)} ${span('articleLists', 1)}"
        "${span('children', 2)}";
        
      ${({ $isReversed }) =>
        $isReversed &&
        css`
        grid-template-areas: 
          "${span('title', 2)}"
          "${span('pinned', 2)}"
          "${span('articleLists', 1)} ${span('hero', 1)}"
          "${span('children', 2)}";
        `};
    `,
  },
  [SectionVariant.TopPicks]: {
    initial: css`
      grid-template-columns: auto;
      grid-template-areas: 
        "title"
        "articleLists";
      .articles-list {
        gap: 16px;
      }
    `,
    mdMin: css`
      .section-title {
        margin-bottom: 20px;
      }
      .articles-list {
        gap: 20px;
      }
    `,
  },
  [SectionVariant.HomeLatestArticles]: {
    initial: css`
      grid-template-columns: auto;
      grid-template-areas: 
        "title"
        "articleLists";
      .articles-list {
        gap: 24px;
      }
    `,
    mdMin: css`
      .articles-list {
        gap: 20px;
      }
    `,
  },
  [SectionVariant.TopArticles]: {
    initial: css`
      grid-template-columns: auto;
      grid-template-areas: 
        "${span('title', 2)}"
        "${span('hero', 2)}"
        "${span('articleLists', 2)}";        
      .articles-list {
        gap: 24px;
      }
    `,
    mdMin: css`
      grid-template-areas: 
        "${span('title', 2)}"
        "${span('hero', 1)} ${span('articleLists', 1)}";
      .articles-list {
        gap: 20px;
      }
    `,
  },
  [SectionVariant.SubSection]: {
    initial: css`
      grid-template-columns: auto;
      grid-template-areas: 
        "title"
        "pinned"
        "articleLists";
      .articles-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }
    `,
    lgMin: css`
      grid-template-columns: auto;
      grid-template-areas: 
        "title"
        "pinned"
        "articleLists";
      .articles-list {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
      }
    `,
  },
  [SectionVariant.SectionLatestArticles]: {
    initial: css`
      grid-template-columns: auto;
      grid-template-areas:
        "title"
        "hero"
        "articleLists2"
        "articleLists";
      .hero-article-card {
        margin-bottom: 24px;
      }
      .articles-list2 {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto 1fr;
      }
      .articles-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }
    `,
    lgMin: css`
      grid-template-columns: repeat(12, 1fr);
      grid-template-rows: auto auto 1fr;
      grid-template-areas: 
        "${span('title', 12)}"
        "${span('hero', 7)} ${span('articleLists', 5)}"
        "${span('articleLists2', 7)} ${span('articleLists', 5)}"; 
      gap: 0 24px;
      .hero-article-card {
        margin-bottom: 40px;
      }
      .articles-list {
        display: flex;
        margin-bottom: 0;
      }
    `,
  },
  [SectionVariant.AuthorArticles]: {
    initial: css`
      grid-template-columns: auto;
      grid-template-areas:
        "title"
        "hero"
        "articleLists2"
        "articleLists";
      .hero-article-card {
        margin-bottom: 24px;
      }
      .articles-list2 {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto 1fr;
      }
      .articles-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }
    `,
    lgMin: css`
      grid-template-columns: repeat(12, 1fr);
      grid-template-rows: auto auto 1fr;
      grid-template-areas: 
        "${span('title', 12)}"
        "${span('hero', 7)} ${span('articleLists', 5)}"
        "${span('articleLists2', 7)} ${span('articleLists', 5)}"; 
      gap: 0 24px;
      .hero-article-card {
        margin-bottom: 40px;
      }
      .articles-list {
        display: flex;
        margin-bottom: 0;
      }
    `,
  },
  [SectionVariant.TopArticles2]: {
    initial: css`
      display: flex;
      flex-direction: column;

      .section-title {
        margin-bottom: var(--Grid-Gap);
      }
    `,
  },
  // used for latest articles on recommends section, subsection, topics pages
  'paginated-content-category-page': {
    initial: css`
      display:flex;
      flex-direction: column;
      .articles-list {
        display: grid;
        grid-template-columns: auto;
      }
      .section-title {
        margin-bottom: 24px;
      }
    `,
    mdMin: css`
      .articles-list {
        grid-template-columns: repeat(3, 1fr);
      }
    `,
    lgMin: css`
      .articles-list {
        grid-template-columns: repeat(4, 1fr);
      }
    `,
  },
  // used for latest articles on recommends homepage, education pages
  'paginated-content': {
    initial: css`
    display: flex;
    flex-direction: column;
    .articles-list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }
    .section-title {
      margin-bottom: 24px;
    }
  `,
    lgMin: css`
    .articles-list {
      grid-template-columns: repeat(4, 1fr);
    }
  `,
  },
  // used on well and crypto homepage
  'paginated-content-3-columns': {
    initial: css`
      grid-template-columns: auto;
      grid-template-areas:
        "title"
        "articleLists"
        "loadMore"
        ;
      .articles-list {
        display: grid;
        grid-template-columns: auto;
      }
      .section-title {
        margin-bottom: 24px;
      }
    `,
    mdMin: css`
      .articles-list {
        grid-template-columns: repeat(3, 1fr);
      }
    `,
    lgMin: css`
      .articles-list {
        grid-template-columns: repeat(3, 1fr);
      }
    `,
  },
};

export const Wrapper = styled.div<{
  $variant: string;
  $isReversed?: boolean;
  $columns?: { default: number };
  theme: Theme;
}>`
  display: grid;
  align-content: start;
  position: relative;
  column-gap: var(--Grid-Gap);

  &.well,
  &.crypto {
    margin-top: 40px;
    padding: 0 16px;

    @media (${breakpoint.mdMin}) {
      padding: 0;
      margin-top: 48px;
    }
  }

  .section-title {
    grid-area: title;
    margin-bottom: 24px;
  }
  .page-info {
    margin-top: -22px;
    margin-bottom: 24px;
  }
  .pinned-links {
    grid-area: pinned;
    margin-top: -8px;
    margin-bottom: 20px;
  }
  .hero-article-card {
    grid-area: hero;
    margin-bottom: 24px;
  }
  .articles-list {
    list-style: none;
    grid-area: articleLists;
    gap: var(--Grid-Gap);
  }
  .articles-list2 {
    grid-area: articleLists2;
    gap: var(--Grid-Gap);
  }
  .children {
    grid-area: children;
  }

  ${({ $variant }) =>
    $variant === 'top-articles' &&
    css<{ theme: Theme }>`
    background: ${getColor('shadeBrandPrimary100')};
    padding: 24px;
  `};
  

  ${({ $variant }) => layoutStyling[$variant].initial || ''};

  @media (${breakpoint.mdMin}) {
    .pinned-links {
      margin-bottom: 24px;
    }
    .hero-article-card {
      margin-bottom: 32px;
    }
    ${({ $variant }) => layoutStyling[$variant].mdMin || ''};
  }

  @media (${breakpoint.lgMin}) {
    .hero-article-card {
      margin-bottom: 0;
    }

    ${({ $variant }) => layoutStyling[$variant].lgMin || ''};
  }

  @media (${breakpoint.xlMin}) {
    ${({ $variant }) => layoutStyling[$variant].xlMin || ''};
  }

  summary::-webkit-details-marker{
      display: none;
  }

  summary{
    cursor: pointer;
    & > h2::after {
      content: url("/icons/BreadcrumbsBlackSeparator.svg");
      transition: transform 0.3s;
      position: absolute;
      right: 0;
      width: 18px;
      height: 18px;
    }
  }

  &[open] > summary > h2::after {
    transform: rotate(180deg);
  }

  &:not([open]) > summary {
    margin-bottom: unset;
  }
`;

export const Title = styled(Heading)<{ theme: Theme; $centered?: boolean; $headerHeight?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px 32px;
  border-bottom: 1px solid ${getColor('borderBlack')};
  list-style-type: none;
  position: relative;
  padding-right: 24px;

  &:empty {
    border-bottom: 0;
  }

  ${({ $centered }) =>
    $centered &&
    css<{ theme: Theme }>`
    position: unset;
    &.section-title{
      margin: 0 auto;
      margin-bottom: 32px;
      padding: 0;
      border-bottom: none;
      h2 {
        border-bottom: 4px solid ${getColor('borderAccentPrimary')};
        background: ${getColor('backgroundPrimary')};
        z-index: 2;
        padding: 0 24px;
      }
      &::before {
        content: ' ';
        position: absolute;
        width: 100%;
        border-bottom: 2px solid ${getColor('borderPrimary')};
        left: 0;
        top: 20px;
      }
    }
  `};

  .pinned {
    margin-bottom: 4px;
  }

  @media (${breakpoint.mdMin}) {
    gap: 4px 40px;
    flex-direction: row;
    .pinned {
      margin-bottom: 0;
    }
  }

  ${({ $headerHeight }) => $headerHeight && { scrollMarginTop: `${$headerHeight}px` }};

  h2 {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    letter-spacing: inherit;
  }
`;

export const PinnedLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 20px; // 8 + 4(width of the ellipse) + 8

  a {
    position: relative;
    font-family: ${font.graphikCond};
    font-size: 16px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0.5px;
    
    &:not(:last-of-type)::after {
      content: url("/icons/Ellipse.svg"); 
      position: absolute;
      top: -2px;
      bottom: 0;
      right: -12px; // 8 + 4(width of the ellipse)
      pointer-events: none;
    }
  }
`;

export const PageInfo = styled(Heading)<{ theme: Theme }>`
  padding-top: 4px;
  align-self: flex-start;
  font-weight: 400;
  line-height: 24px;
`;

export const ArticlesList = styled.div<{
  theme: Theme;
}>`
  display: flex;
  flex-direction: column;

  .top-articles-card {
    .card-date {
      color: ${getColor('textPrimary')};
      font-weight: 400;
    }
  }

  .top-articles-card:not(:last-child) {
    padding-bottom: var(--Spacings-Spacing-20, 20px);
    border-bottom: 1px solid var(--Border-Secondary, #D1D1D6);
  }
`;

export const Children = styled.div<{ theme: Theme }>`
    background-color: ${getColor('surfaceWhite')};
    box-shadow: 4px 4px 20px 0px ${getColor('textPrimary')}0D;
    padding: 24px;
    margin-top: 24px;
`;
